import * as React from 'react'

import { AvailableModules } from 'generated/featureDefinitions'
import {
  GetFeatureAvailabilitiesDocument,
  ShiftManagementFeaturesFlagsDocument,
  ShiftManagementPageDocument,
} from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
// import type { FeatureAvailabilityType } from 'modules/core/hooks/useFeatureAvailability'
import { useQuery } from 'modules/core/hooks/useQuery'

// TODO: To be removed once the migration to GraphQL is done
import jobLevelsCollection from 'modules/job_catalog/store/collections/levels'
import jobRolesCollection from 'modules/job_catalog/store/collections/roles'
import { ShiftManagement } from 'modules/shift_management/components/ShiftManagement'
import { EnabledFeaturesProvider } from 'modules/shift_management/hooks/useEnabledFeatures'
import { FiltersProvider } from 'modules/shift_management/hooks/useFilters'
import useIsTimeFormat24H from 'modules/shift_management/hooks/useIsTimeFormat24H'
import { NotificationsProvider } from 'modules/shift_management/hooks/useNotifications'
import { PaginationProvider } from 'modules/shift_management/hooks/usePagination'
import { PermissionsProvider } from 'modules/shift_management/hooks/usePermissions'
import { PreviewProvider } from 'modules/shift_management/hooks/usePreview'
import { ScheduleProvider } from 'modules/shift_management/hooks/useSchedule'
import { SelectionProvider } from 'modules/shift_management/hooks/useSelection'
import { ShiftsRowsProvider } from 'modules/shift_management/hooks/useShiftsRows'
import { TemplateWeeksProvider } from 'modules/shift_management/hooks/useTemplateWeeks'
import { UIProvider } from 'modules/shift_management/hooks/useUI'
import { UnassignedShiftsProvider } from 'modules/shift_management/hooks/useUnassignedShifts'
import { ViewOptionsProvider } from 'modules/shift_management/hooks/useViewOptions'
// TODO: Pending migration to graphql
import planningVersions, {
  PlanningVersionProvider,
} from 'modules/time_planning/store/collections/planningVersions'

const ShiftManagementMainPage = ({ children }: React.PropsWithChildren) => {
  const { loading: loadingJobs } = useAsyncData(
    () => Promise.all([jobRolesCollection.fetch(), jobLevelsCollection.fetch()]),
    []
  )
  const { loading: loadingPlanningVersions } = useAsyncData(
    () => planningVersions.fetch({ data: { for_shifts: true } }),
    []
  )

  const { isTimeFormat24H } = useIsTimeFormat24H()
  const { company: currentCompany } = useCurrentSession()
  const { loading: loadingFeatures, data: featuresData } = useQuery(
    GetFeatureAvailabilitiesDocument,
    {
      fetchPolicy: 'cache-first',
      variables: {
        modules: [AvailableModules.shift_management, AvailableModules.time_balance],
        companyId: currentCompany.id,
      },
    }
  )

  const { loading: loadingFeatureFlags, data: featureFlagsData } = useQuery(
    ShiftManagementFeaturesFlagsDocument,
    {
      fetchPolicy: 'cache-first',
      variables: {
        featureIds: [
          'legacy_job_catalog',
          'dev_location_work_areas',
          'dev_shift_management_automation',
          'dev_shift_management_graphql_migration',
          'dev_shift_management_over_planned_alert',
          'dev_time_planning_balances_in_shifts',
          'dev_time_planning_bank_of_hours',
          'shift_management_area_view',
          'shift_management_backup_shifts',
          'shift_management_colours_for_shifts',
          'shift_management_daily_view',
          'shift_management_monthly_view',
          'shift_management_rest_day',
          'time_settings_breaks',
          'timeoff_policy_timeline',
          'dev_shift_management_new_employees_selector',
          'dev_shift_management_shifts_table_abstraction',
          'dev_time_tracking_complementary_hours',
        ],
        companyId: currentCompany.id,
      },
    }
  )

  const { data: permissions, loading: loadingPermissions } = useQuery(
    ShiftManagementPageDocument,
    {}
  )

  const featureAvailabilities = featuresData?.companies.featureAvailabilities || []
  const featureFlags = featureFlagsData?.features.featuresConnection.nodes || []
  const isLoading =
    loadingJobs ||
    loadingPlanningVersions ||
    loadingFeatures ||
    loadingPermissions ||
    loadingFeatureFlags

  if (isLoading) return <Loading />

  return (
    <EnabledFeaturesProvider features={featureAvailabilities} featureFlags={featureFlags}>
      <PermissionsProvider permissions={permissions}>
        <TemplateWeeksProvider>
          <NotificationsProvider>
            <UnassignedShiftsProvider>
              <PlanningVersionProvider collection={planningVersions}>
                <ScheduleProvider isTimeFormat24H={isTimeFormat24H}>
                  <ViewOptionsProvider>
                    <FiltersProvider>
                      <PaginationProvider>
                        <ShiftsRowsProvider>
                          <UIProvider>
                            <SelectionProvider>
                              <PreviewProvider>
                                <ShiftManagement>{children}</ShiftManagement>
                              </PreviewProvider>
                            </SelectionProvider>
                          </UIProvider>
                        </ShiftsRowsProvider>
                      </PaginationProvider>
                    </FiltersProvider>
                  </ViewOptionsProvider>
                </ScheduleProvider>
              </PlanningVersionProvider>
            </UnassignedShiftsProvider>
          </NotificationsProvider>
        </TemplateWeeksProvider>
      </PermissionsProvider>
    </EnabledFeaturesProvider>
  )
}

export default ShiftManagementMainPage
